import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { consoleApi } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { NavLink, useRouter } from '../lib/router';
import { SCHEDULE_PATH, workspaceNavigation, workspaceSection } from '../lib/navigation';
import { Icon } from './Icon';
import './section-navigation.css';

export function Shell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const { pathname, search } = useRouter();
  const section = workspaceSection(pathname, search);
  const status = useQuery({ queryKey: ['status'], queryFn: consoleApi.status, refetchInterval: 60_000 });
  const scheduler = status.isError ? 'Status unavailable' : status.isLoading ? 'Checking scheduler…'
    : !status.data?.schedulerEnabled ? 'Scheduler off' : status.data.schedulerRunning === true ? 'Scheduler running'
      : status.data.schedulerRunning === false ? 'Scheduler stopped' : 'Scheduler unconfirmed';
  const [logoutError, setLogoutError] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  async function signOut() {
    setLogoutError(false); setLoggingOut(true);
    try { await logout(); } catch { setLogoutError(true); } finally { setLoggingOut(false); }
  }
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 850px)').matches);
  const sidebar = useRef<HTMLElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 850px)');
    const update = () => { setMobile(media.matches); setMobileOpen(false); };
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  useEffect(() => {
    if (sidebar.current) sidebar.current.inert = mobile && !mobileOpen;
    if (content.current) content.current.inert = mobile && mobileOpen;
    if (!mobile || !mobileOpen) return;
    const beforeOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const controls = () => Array.from(sidebar.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') || []);
    controls()[0]?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); setMobileOpen(false); }
      if (event.key === 'Tab') {
        const items = controls(), first = items[0], last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.removeEventListener('keydown', keydown);
      document.body.style.overflow = beforeOverflow;
      if (content.current) content.current.inert = false;
      if (menu.current?.offsetParent) menu.current.focus();
    };
  }, [mobile, mobileOpen]);

  return (
    <div className="console-shell">
      <aside ref={sidebar} id="console-navigation" aria-label="Navigation" className={'sidebar' + (mobileOpen ? ' sidebar--open' : '')}>
        <button type="button" className="drawer-close button button--quiet" onClick={() => setMobileOpen(false)}>Close navigation</button>
        <div className="brand">
          <span className="brand__mark">LE</span>
          <span><strong>LiquidEdge</strong><small>GTM Console</small></span>
        </div>
        <nav aria-label="Console sections" className="sidebar__nav">
          {workspaceNavigation.map((item) => (
            <NavLink end key={item.to} active={section === item} onNavigate={() => setMobileOpen(false)} to={item.to} className={'nav-link' + (section === item ? ' nav-link--active' : '')}>
              <Icon name={item.icon} /><span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar__footer">
          <NavLink to="/settings" onNavigate={() => setMobileOpen(false)} className={active => 'nav-link' + (active ? ' nav-link--active' : '')}><Icon name="settings" /><span>Settings</span></NavLink>
          <NavLink to="/help" onNavigate={() => setMobileOpen(false)} className={active => 'nav-link' + (active ? ' nav-link--active' : '')}><Icon name="help" /><span>Help</span></NavLink>
        </div>
      </aside>
      {mobileOpen ? <button aria-label="Close navigation" className="sidebar-scrim" onClick={() => setMobileOpen(false)} type="button" /> : null}
      <div ref={content} className="console-content">
        <a className="console-skip" href="#console-main">Skip to content</a>
        <header className="topbar">
          <button ref={menu} aria-controls="console-navigation" aria-expanded={mobileOpen} aria-label="Open navigation" className="menu-button" onClick={() => setMobileOpen(true)} type="button"><Icon name="menu" /></button>
          <div className="topbar__context"><span>Private GTM workspace</span></div>
          <div className="profile">
            <NavLink to={SCHEDULE_PATH} className={'scheduler-indicator' + (!status.isError && status.data?.schedulerEnabled && status.data.schedulerRunning === true ? ' scheduler-indicator--running' : '')}><Icon name="clock" /><span>{scheduler}</span></NavLink>
            <span className="profile__copy"><strong>{user?.displayName}</strong></span>
            <button className="button button--quiet button--small" disabled={loggingOut} onClick={() => void signOut()} type="button">{loggingOut ? 'Logging out…' : 'Log out'}</button>
          </div>
        </header>
        <main id="console-main" tabIndex={-1} className="page">
          {section?.links.length && pathname !== '/' ? <nav className="section-navigation" aria-label={section.label + ' pages'}>{section.links.map(item => <NavLink key={item.to} to={item.to} active={item.paths.includes(pathname)} className={active => 'section-navigation__link' + (active ? ' section-navigation__link--active' : '')}><Icon name={item.icon} /><span>{item.label}</span></NavLink>)}</nav> : null}
          {logoutError ? <p className="inline-alert inline-alert--error" role="alert">Log out failed. Your session may still be active. Check your connection and try Log out again.</p> : null}{children}
        </main>
      </div>
    </div>
  );
}

export function PageHeader({ title, description, action }: { eyebrow?: string; title: string; description: string; action?: ReactNode }) {
  return (
    <header className="page-header">
      <div><h1>{title}</h1><p>{description}</p></div>
      {action ? <div className="page-header__action">{action}</div> : null}
    </header>
  );
}
